import { Link } from "react-router-dom";
import { BorderButton } from "src/components/buttons/BorderButton";
import Breadcrumbs from "src/components/layout/Breadcrumbs";
import Container from "src/components/layout/Container";
import { useStore } from "src/stores/Store";
import { useListBackground } from "src/utils/setListBackground";
import tw from "src/utils/tw";
import { UsaSwimmingParticipant } from "types/usa-swimming";

export default function UsaSwimmingSync() {
  const store = useStore();
  useListBackground();

  const accounts = store.organization.usaSwimmingImport?.accounts || [];

  const participants: UsaSwimmingParticipant[] = accounts.reduce((acc, a) => {
    return acc.concat(a.participants);
  }, []);

  const accountMatches = accounts.filter((p) => p.matched);
  const accountMatchesForReview = accountMatches.filter((a) => !a.reviewed);
  const missingEmail = accounts.filter((p) => !p.email);

  const participantMatches = participants.filter((p) => p.matched);
  const participantMatchesForReview = participantMatches.filter(
    (a) => !a.reviewed
  );

  const canReviewParticipants = accountMatchesForReview.length === 0;
  const canSync =
    participantMatchesForReview.length === 0 &&
    accountMatchesForReview.length === 0;

  return (
    <Container full main className="bg-white pb-12">
      <div className="border-b border-borderGray px-4 pb-4">
        <Breadcrumbs>
          <Link to="/admin">Home</Link> /{" "}
          <Link to="/admin/settings">Settings</Link> /{" "}
          <Link to="/admin/account">General Settings</Link> /{" "}
          <strong>USA Swimming</strong>
        </Breadcrumbs>

        <h1 className="mb-0 sm:mb-2">USA Swimming</h1>
      </div>

      <Container large className="pt-6">
        <p>
          <strong>Let’s keep it simple</strong>
          <br />
          Captyn will sync your USA Swimming members, allowing you to manage
          your training groups.
        </p>

        <div>
          <div className="flex items-start">
            <i className="far fa-circle-check relative bg-white text-success" />
            <div className="-ml-2 flex-1 border-l border-borderGray pb-4 pl-4 leading-none">
              <strong>Manage your USA Swimming Club ID</strong>
            </div>
          </div>

          <div className="flex items-start">
            <i className="far fa-circle-check relative bg-white text-success" />
            <div className="-ml-2 flex-1 border-l border-borderGray pb-4 pl-4">
              <strong className="block pb-2 leading-none">
                {store.organization.usaSwimmingImport?.clubName}
              </strong>

              <div className="pb-2 text-sm">
                Your team, according to USA Swimming:
                <div>
                  <i className="far fa-users-gear" />{" "}
                  {accounts.length.toLocaleString()} total accounts
                  {missingEmail.length > 0 &&
                    ` • ${missingEmail.length.toLocaleString()} missing email`}
                </div>
                <div>
                  <i className="far fa-person-swimming" />{" "}
                  {participants.length.toLocaleString()} total swimmers
                </div>
              </div>

              <BorderButton to={`/admin/usa-swimming/preview`}>
                Preview{" "}
                <i className="far fa-arrow-up-right-from-square ml-2 text-organization" />
              </BorderButton>
            </div>
          </div>

          {accountMatches.length > 0 && (
            <div className="flex items-start">
              <i
                className={tw(
                  "far relative bg-white",
                  accountMatchesForReview.length > 0
                    ? "fa-circle text-organization"
                    : "fa-circle-check text-success"
                )}
              />
              <div className="-ml-2 flex-1 border-l border-borderGray pb-4 pl-4">
                <strong className="block pb-2 leading-none">
                  Review potential account matches
                </strong>

                <div className="pb-2 text-sm">
                  We think there may be some accounts from USA Swimming that
                  already exist in Captyn.
                  <div>
                    <i className="far fa-notes" />{" "}
                    {accountMatchesForReview.length.toLocaleString()} potential
                    matches to review
                  </div>
                </div>
                <div>
                  <BorderButton
                    type={
                      accountMatchesForReview.length > 0 ? "submit" : undefined
                    }
                    to={`/admin/usa-swimming/review/accounts`}
                  >
                    {accountMatchesForReview.length > 0
                      ? "Review"
                      : "Review Matches"}
                  </BorderButton>
                </div>
              </div>
            </div>
          )}

          {participantMatches.length > 0 && (
            <div className="flex items-start">
              <i
                className={tw(
                  "far relative bg-white",
                  !canReviewParticipants && "fa-circle text-empty",
                  canReviewParticipants &&
                    participantMatchesForReview.length > 0 &&
                    "fa-circle text-organization",
                  canReviewParticipants &&
                    participantMatchesForReview.length === 0 &&
                    "fa-circle-check text-success"
                )}
              />
              <div className="-ml-2 flex-1 border-l border-borderGray pb-4 pl-4">
                <strong
                  className={tw(
                    "block pb-2 leading-none",
                    canReviewParticipants ? "text-dark" : "text-empty"
                  )}
                >
                  Review potential swimmer matches
                </strong>

                {canReviewParticipants && (
                  <>
                    <div className="pb-2 text-sm">
                      Based on your accounts, we think some of your swimmers
                      also already exist in Captyn.
                      <div>
                        <i className="far fa-notes" />{" "}
                        {participantMatchesForReview.length.toLocaleString()}{" "}
                        potential matches to review
                      </div>
                    </div>
                    <div>
                      <BorderButton
                        type={
                          participantMatchesForReview.length > 0
                            ? "submit"
                            : undefined
                        }
                        to={`/admin/usa-swimming/review/participants`}
                      >
                        {accountMatchesForReview.length > 0
                          ? "Review"
                          : "Review Matches"}
                      </BorderButton>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex items-start">
            <i
              className={tw(
                "far fa-circle bg-white",
                canSync ? "text-organization" : "text-empty"
              )}
            />
            <div className="-ml-2 flex-1 pl-4">
              <strong
                className={tw(
                  "block pb-2 leading-none",
                  canSync ? "text-dark" : "text-empty"
                )}
              >
                Continue to sync
              </strong>

              {canSync && (
                <div>
                  <BorderButton
                    type="submit"
                    to={`/admin/usa-swimming/notification`}
                    onClick={() => {}}
                  >
                    Continue
                  </BorderButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </Container>
  );
}
